import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { useReportMetodosPago } from '@/hooks/useReports'
import { FiltrosReporte } from '@/services/reportService'
import ReportCard from './ReportCard'
import ReportTable from './ReportTable'
import { ResponsivePie } from '@nivo/pie'

interface MetodosPagoReportProps {
  filtros: FiltrosReporte
  onRefresh?: () => void
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <p className="text-gray-600 font-medium">Error al renderizar el gráfico</p>
            <p className="text-sm text-gray-500 mt-2">Intenta actualizar la página</p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const MetodosPagoReport: React.FC<MetodosPagoReportProps> = ({ filtros, onRefresh }) => {
  const { colors } = useTheme()
  const { isMobile } = useDeviceDetection()
  const { data: reportData, isLoading, error, refetch } = useReportMetodosPago(filtros)

  const handleRefresh = () => {
    refetch()
    onRefresh?.()
  }

  const getErrorMessage = () => {
    if (!error) return undefined
    const errorString = error instanceof Error ? error.message : String(error)
    return errorString
  }

  const handleExport = () => {
    if (!reportData?.datos) return

    const csv = [
      ['Método de pago', 'Cantidad', 'Monto total', 'Porcentaje'].join(','),
      ...reportData.datos.map((d) =>
        [d.metodo_pago, d.cantidad, d.monto_total.toFixed(2), d.porcentaje.toFixed(2)].join(',')
      )
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `metodos-pago-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const chartData = reportData?.datos?.map(item => {
    const cantidad = Number(item.cantidad) || 0
    const monto = Number(item.monto_total) || 0
    const porcentaje = Number(item.porcentaje) || 0

    return {
      metodo_pago: item.metodo_pago,
      id: item.metodo_pago,
      label: item.metodo_pago,
      value: cantidad,
      cantidad: cantidad,
      monto_total: Number.isFinite(monto) ? monto : 0,
      porcentaje: Number.isFinite(porcentaje) ? porcentaje : 0
    }
  }) || []

  const tableColumns = [
    { key: 'metodo_pago' as const, label: 'Método de pago' },
    { key: 'cantidad' as const, label: 'Cantidad de pagos' },
    {
      key: 'monto_total' as const,
      label: 'Monto total',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return `$${validValue.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
      }
    },
    {
      key: 'porcentaje' as const,
      label: 'Porcentaje',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return (
          <div className="flex items-center gap-2">
            <span>{validValue.toFixed(1)}%</span>
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${validValue}%`,
                  backgroundColor: colors.primary
                }}
              ></div>
            </div>
          </div>
        )
      }
    }
  ]

  const pieData = (reportData?.datos || [])
    .filter((d) => {
      const monto = Number(d.monto_total) || 0
      return monto > 0 && Number.isFinite(monto)
    })
    .map((d) => {
      const monto = Number(d.monto_total) || 0
      return {
        id: d.metodo_pago.toUpperCase(),
        label: d.metodo_pago.toUpperCase(),
        value: monto
      }
    })

  // Colores basados en el tema
  const chartColors = [
    colors.primary,
    colors.primaryLight,
    colors.primaryDark,
    '#d4a574',
    '#b8860b'
  ]

  return (
    <ReportCard
      title="Métodos de Pago"
      subtitle="Distribución de ingresos por método de pago"
      loading={isLoading}
      error={getErrorMessage()}
      onExport={handleExport}
      chartComponent={
        pieData.length > 0 ? (
          <ErrorBoundary>
            <div style={{ width: '100%', height: isMobile ? '400px' : '500px', minWidth: 0 }}>
              <ResponsivePie
              data={pieData}
              margin={isMobile
                ? { top: 20, right: 20, bottom: 80, left: 20 }
                : { top: 20, right: 150, bottom: 20, left: 20 }
              }
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 0.2]],
              }}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={colors.primaryDark}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              arcLabelsSkipAngle={10}
              arcLabelsTextColor={{
                from: 'color',
                modifiers: [['darker', 1.5]],
              }}
              colors={chartColors}
              defs={[
                {
                  id: 'dots',
                  type: 'patternDots',
                  background: 'inherit',
                  color: 'rgba(255, 255, 255, 0.3)',
                  size: 4,
                  padding: 1,
                  stagger: true,
                },
                {
                  id: 'lines',
                  type: 'patternLines',
                  background: 'inherit',
                  color: 'rgba(255, 255, 255, 0.3)',
                  rotation: -45,
                  lineWidth: 6,
                  spacing: 10,
                },
              ]}
              fill={[
                {
                  match: {
                    id: 'ruby',
                  },
                  id: 'dots',
                },
                {
                  match: {
                    id: 'c',
                  },
                  id: 'dots',
                },
                {
                  match: {
                    id: 'go',
                  },
                  id: 'dots',
                },
                {
                  match: {
                    id: 'python',
                  },
                  id: 'lines',
                },
                {
                  match: {
                    id: 'rust',
                  },
                  id: 'lines',
                },
              ]}
              legends={[
                {
                  anchor: isMobile ? 'bottom' : 'right',
                  direction: isMobile ? 'row' : 'column',
                  justify: false,
                  translateX: isMobile ? 0 : 130,
                  translateY: isMobile ? 60 : 0,
                  itemsSpacing: isMobile ? 4 : 8,
                  itemWidth: isMobile ? 80 : 100,
                  itemHeight: 18,
                  itemTextColor: colors.primaryDark,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.9,
                  symbolSize: isMobile ? 12 : 14,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemTextColor: colors.primary,
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
              tooltip={({ datum }) => {
                // Encontrar el porcentaje correspondiente
                const originalData = reportData?.datos?.find(d => d.metodo_pago.toUpperCase() === datum.id)
                const porcentaje = originalData?.porcentaje || 0
                const cantidad = originalData?.cantidad || 0

                return (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      color: colors.primaryDark,
                      borderRadius: '8px',
                      border: `2px solid ${colors.primary}`,
                      fontSize: '12px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <strong style={{ color: colors.primary, display: 'block', marginBottom: '4px' }}>
                      {datum.label}
                    </strong>
                    <div style={{ color: colors.primaryDark }}>
                      <div>Monto: ${datum.value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}</div>
                      <div>Pagos: {cantidad}</div>
                      <div>Porcentaje: {porcentaje.toFixed(1)}%</div>
                    </div>
                  </div>
                )
              }}
              />
            </div>
          </ErrorBoundary>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No hay datos de pagos
          </div>
        )
      }
      tableComponent={<ReportTable columns={tableColumns} data={reportData?.datos || []} />}
    >
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Métodos activos</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {chartData.length}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Total pagos</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {chartData.reduce((sum, d) => sum + (d.cantidad || 0), 0)}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Monto total</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              ${chartData
                .reduce((sum, d) => sum + (d.monto_total || 0), 0)
                .toLocaleString('es-CL', { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}
    </ReportCard>
  )
}

export default MetodosPagoReport
